"use client";

import { ArrowRight, Globe, InstagramLogo, X, YoutubeLogo } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import type { Locale } from "@/i18n/config";

type LeadersTranslations = {
  sectionLabel: string;
  title: string;
  titleHighlight: string;
  description: string;
  readBio: string;
  leadershipBio: string;
  returnToLeaders: string;
  close: string;
  roles: {
    nationalPastor: string;
    globalYouthPastor: string;
    nationalYouthPastor: string;
  };
  focus: {
    missionaryStrategy: string;
    globalYouthEmpowerment: string;
    spiritualGrowth: string;
  };
  bios: {
    davidOladosu: string;
    yemiAwodiran: string;
    steveOgah: string;
    aanuoluwapoAkinyera: string;
  };
};

type SocialTranslations = {
  joinMovement: string;
  description: string;
  instagram: string;
  followInstagram: string;
  youtube: string;
  watchAndLike: string;
  nationalChurch: string;
  winnersChapel: string;
  globalYouth: string;
  youthAliveGlobal: string;
};

type LeaderData = {
  name: string;
  roleKey: keyof LeadersTranslations['roles'];
  image: string;
  focusKey: keyof LeadersTranslations['focus'];
  bioKey: keyof LeadersTranslations['bios'];
};

const leadersData: LeaderData[] = [
  {
    name: "Pastor Yemi Awodiran",
    roleKey: "nationalPastor",
    image: "/ayac/ayacleaders/pastor-yemi-awodiran-revival-summit.png",
    focusKey: "missionaryStrategy",
    bioKey: "yemiAwodiran",
  },
  {
    name: "Pastor Steve Ogah",
    roleKey: "globalYouthPastor",
    image: "/ayac/ayacleaders/pastor-steve-ogah-revival-summit.png",
    focusKey: "globalYouthEmpowerment",
    bioKey: "steveOgah",
  },
  {
    name: "Pastor Aanuoluwapo Akinyera",
    roleKey: "nationalYouthPastor",
    image: "/ayac/ayacleaders/pastor-aanu-akinyera-revival-summit.png",
    focusKey: "spiritualGrowth",
    bioKey: "aanuoluwapoAkinyera",
  },
];

type LeadersSectionProps = {
  locale?: Locale; // Kept for API compatibility
  translations?: LeadersTranslations;
  socialTranslations?: SocialTranslations;
};

export const LeadersSection = ({ translations, socialTranslations }: LeadersSectionProps) => {
  const [selectedLeader, setSelectedLeader] = useState<number | null>(null);

  // Default translations for backwards compatibility
  const t: LeadersTranslations = translations || {
    sectionLabel: "The Oversight",
    title: "Led by",
    titleHighlight: "The Spirit",
    description: "Meet the visionary leaders committed to raising a generation of giants across Canada and beyond.",
    readBio: "Read Bio",
    leadershipBio: "Leadership Bio",
    returnToLeaders: "Return to Leaders",
    close: "Close",
    roles: {
      nationalPastor: "National Pastor, Canada",
      globalYouthPastor: "Global Youth Pastor",
      nationalYouthPastor: "National Youth Pastor, Canada",
    },
    focus: {
      missionaryStrategy: "Missionary Strategy & Vision",
      globalYouthEmpowerment: "Global Youth Empowerment",
      spiritualGrowth: "Spiritual Growth & Leadership",
    },
    bios: {
      davidOladosu: "Pastor David Oladosu serves as the National Pastor for the Mission to Canada, overseeing multiple Winners Chapel International churches while also functioning as a resident pastor and conference speaker. His ministry emphasizes strong Word-based teaching, multicultural outreach and church planting, helping to strengthen the denomination's footprint across Canadian cities. As a key leader in the Living Faith Church Worldwide, he serves a growing network of young believers and vibrant congregations across Canada and beyond.",
      yemiAwodiran: "Pastor Yemi Awodiran serves as the National Pastor for the Mission in Canada.",
      steveOgah: "Pastor Steve Ogah is the National Youth Pastor of Living Faith Church Worldwide.",
      aanuoluwapoAkinyera: "Pastor Aanuoluwapo Akinyera is the National Youth Pastor for Canada.",
    },
  };

  // Default social translations for backwards compatibility
  const social: SocialTranslations = socialTranslations || {
    joinMovement: "Join the Movement",
    description: "Stay connected for daily inspiration and updates from our community.",
    instagram: "Instagram",
    followInstagram: "Follow @yafcanada",
    youtube: "YouTube",
    watchAndLike: "Watch & Like",
    nationalChurch: "National Church",
    winnersChapel: "Winners Chapel Intl Canada",
    globalYouth: "Global Youth",
    youthAliveGlobal: "Youth Alive Global",
  };

  // Create locale-aware leaders array
  const leaders = leadersData.map(leader => ({
    name: leader.name,
    role: t.roles[leader.roleKey],
    image: leader.image,
    focus: t.focus[leader.focusKey],
    bio: t.bios[leader.bioKey],
  }));

  useEffect(() => {
    const body = document.body;
    if (selectedLeader !== null) {
      body.classList.add("modal-open");
    } else {
      body.classList.remove("modal-open");
    }

    return () => body.classList.remove("modal-open");
  }, [selectedLeader]);

  return (
    <section id="leadership" className="py-40 bg-white dark:bg-black transition-colors relative overflow-x-clip overflow-y-visible">
      {/* Background Decorative Text */}
      <div className="absolute top-40 -left-20 opacity-[0.03] dark:opacity-[0.05] pointer-events-none select-none z-0">
        <span className="text-[30vw] font-serif italic">Vision</span>
      </div>

      {/* Background Glows */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-brand-red/5 dark:bg-brand-red/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-brand-gold/5 dark:bg-brand-gold/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-32 gap-8">
          <div>
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-brand-gold font-black uppercase tracking-[0.5em] text-[10px] mb-6 block"
            >
              {t.sectionLabel}
            </motion.span>
            <h2 className="text-6xl md:text-8xl font-serif text-black dark:text-white leading-none tracking-tighter">
              {t.title} <br />
              <span className="italic text-brand-red">{t.titleHighlight}</span>
            </h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-zinc-500 dark:text-zinc-400 max-w-sm text-lg font-light leading-relaxed"
          >
            {t.description}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {leaders.map((leader, i) => (
            <motion.div
              key={leader.name}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.2 }}
              className="group relative cursor-pointer"
              onClick={() => setSelectedLeader(i)}
            >
              <div className="relative aspect-square rounded-[2rem] overflow-hidden mb-8 shadow-lg transition-transform duration-500 group-hover:-translate-y-2">
                <Image
                  src={leader.image}
                  fill
                  alt={leader.name}
                  className="object-cover transition-transform duration-700 group-hover:scale-[1.025]"
                  sizes="(max-width: 768px) 100vw, 33vw"
                />
              </div>

              <div className="space-y-3">
                <h3 className="text-3xl font-serif text-black dark:text-white italic group-hover:text-brand-gold transition-colors">
                  {leader.name}
                </h3>
                <p className="text-[11px] font-bold text-brand-red uppercase tracking-[0.2em]">{leader.role}</p>
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors group-hover:text-brand-red dark:text-zinc-500">
                  <span>{t.readBio}</span>
                  <ArrowRight weight="bold" className="w-3 h-3" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bio Drawer Overlay */}
        <AnimatePresence>
          {selectedLeader !== null && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedLeader(null)}
                className="fixed inset-0 bg-black/60 backdrop-blur-md z-[200] cursor-pointer"
              />
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="fixed bottom-0 left-0 right-0 z-[201] bg-white dark:bg-zinc-950 rounded-t-[3rem] max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
              >
                {/* Fixed Close Button */}
                <div className="absolute top-6 right-6 md:top-10 md:right-10 z-[210]">
                  <button
                    onClick={() => setSelectedLeader(null)}
                    className="inline-flex items-center gap-2 rounded-full border border-zinc-200 dark:border-white/10 px-4 py-2 text-zinc-500 hover:text-brand-red transition-colors bg-white/80 dark:bg-zinc-900/70 backdrop-blur-md shadow-sm"
                  >
                    <X weight="bold" className="w-4 h-4" />
                    <span className="text-[10px] font-black uppercase tracking-widest">{t.close}</span>
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto overscroll-contain p-10 md:p-20 pt-20 md:pt-24">
                  <div className="max-w-4xl mx-auto relative">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                      <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
                        <Image
                          src={leaders[selectedLeader].image}
                          fill
                          alt={leaders[selectedLeader].name}
                          className="object-cover"
                        />
                      </div>
                      <div className="space-y-8">
                        <div>
                          <span className="text-brand-red font-black uppercase tracking-[0.3em] text-[10px] mb-4 block">
                            {t.leadershipBio}
                          </span>
                          <h3 className="text-5xl md:text-6xl font-serif text-black dark:text-white italic leading-none">
                            {leaders[selectedLeader].name}
                          </h3>
                          <p className="text-brand-gold font-bold uppercase tracking-[0.2em] text-xs mt-4">
                            {leaders[selectedLeader].role}
                          </p>
                        </div>

                        <div className="h-px w-20 bg-brand-red" />

                        <p className="text-xl text-zinc-600 dark:text-zinc-400 font-light leading-relaxed">
                          {leaders[selectedLeader].bio}
                        </p>

                        <div className="pt-8">
                          <button
                            onClick={() => setSelectedLeader(null)}
                            className="bg-black dark:bg-white text-white dark:text-black px-8 py-4 rounded-full font-black uppercase text-[10px] tracking-widest hover:bg-brand-red dark:hover:bg-brand-red hover:text-white transition-all"
                          >
                            {t.returnToLeaders}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Social Media Links */}
        <div id="watch" className="mt-32 flex flex-col items-center relative">
          {/* Background Decorative "Burning" Text */}
          <div className="absolute -top-52 left-1/2 -translate-x-1/2 pointer-events-none select-none z-0 flex justify-center whitespace-nowrap">
            <motion.span
              animate={{
                opacity: [0.2, 0.4, 0.3, 0.5, 0.25],
                scale: [1, 1.05, 1, 1.02, 1],
                filter: [
                  "blur(8px) brightness(1) drop-shadow(0 0 20px rgba(239, 68, 68, 0.4))",
                  "blur(12px) brightness(1.3) drop-shadow(0 0 40px rgba(239, 68, 68, 0.6))",
                  "blur(8px) brightness(1.1) drop-shadow(0 0 25px rgba(239, 68, 68, 0.4))",
                ],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="text-[35vw] font-serif italic leading-none text-transparent bg-clip-text bg-gradient-to-t from-brand-red via-brand-gold to-white/20"
            >
              Fire
            </motion.span>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 relative z-10"
          >
            <h4 className="text-3xl font-serif italic text-black dark:text-white mb-3">{social.joinMovement}</h4>
            <p className="text-zinc-500 dark:text-zinc-400 text-lg font-light">
              {social.description}
            </p>
          </motion.div>

          <div className="flex flex-col sm:flex-row gap-8">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                href="https://www.instagram.com/yafcanada/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-6 px-10 py-6 rounded-3xl border border-zinc-200 dark:border-white/10 hover:bg-brand-red hover:border-brand-red transition-all duration-500 shadow-xl hover:shadow-brand-red/40"
              >
                <div className="w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-white/5 flex items-center justify-center group-hover:bg-white/20 transition-colors duration-500">
                  <InstagramLogo weight="fill" className="w-8 h-8 text-black dark:text-white group-hover:text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 dark:text-zinc-400 group-hover:text-white/70">
                    {social.instagram}
                  </span>
                  <span className="text-xl font-serif italic text-black dark:text-white group-hover:text-white">
                    {social.followInstagram}
                  </span>
                </div>
              </Link>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                href="/watch"
                className="group flex items-center gap-6 px-10 py-6 rounded-3xl border border-zinc-200 dark:border-white/10 hover:bg-brand-red hover:border-brand-red transition-all duration-500 shadow-xl hover:shadow-brand-red/40"
              >
                <div className="w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-white/5 flex items-center justify-center group-hover:bg-white/20 transition-colors duration-500">
                  <YoutubeLogo weight="fill" className="w-8 h-8 text-black dark:text-white group-hover:text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 dark:text-zinc-400 group-hover:text-white/70">
                    {social.youtube}
                  </span>
                  <span className="text-xl font-serif italic text-black dark:text-white group-hover:text-white">
                    {social.watchAndLike}
                  </span>
                </div>
              </Link>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                href="https://winnerschapelcanada.ca/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-6 px-10 py-6 rounded-3xl border border-zinc-200 dark:border-white/10 hover:bg-brand-gold hover:border-brand-gold transition-all duration-500 shadow-xl hover:shadow-brand-gold/40"
              >
                <div className="w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-white/5 flex items-center justify-center group-hover:bg-white/20 transition-colors duration-500">
                  <Image
                    src="/new2025logo.png"
                    alt="Winners Chapel"
                    width={32}
                    height={32}
                    className="w-8 h-8 object-contain grayscale dark:invert dark:brightness-100 group-hover:brightness-0 group-hover:invert transition-all duration-500"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 dark:text-zinc-400 group-hover:text-white/70">
                    {social.nationalChurch}
                  </span>
                  <span className="text-xl font-serif italic text-black dark:text-white group-hover:text-white">
                    {social.winnersChapel}
                  </span>
                </div>
              </Link>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                href="https://winnersyouth.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-6 px-10 py-6 rounded-3xl border border-zinc-200 dark:border-white/10 hover:bg-brand-red hover:border-brand-red transition-all duration-500 shadow-xl hover:shadow-brand-red/40"
              >
                <div className="w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-white/5 flex items-center justify-center group-hover:bg-white/20 transition-colors duration-500">
                  <Globe weight="bold" className="w-8 h-8 text-black dark:text-white group-hover:text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 dark:text-zinc-400 group-hover:text-white/70">
                    {social.globalYouth}
                  </span>
                  <span className="text-xl font-serif italic text-black dark:text-white group-hover:text-white">
                    {social.youthAliveGlobal}
                  </span>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
